import { Tooltip } from '@mantine/core';
import { createReference, getReferenceString } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import dayjs from 'dayjs';
import { MouseEvent, ReactElement, useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { alert } from '@/services/alert';
import { createStandaloneNote, updateTimelineNote } from '@/services/timeline-notes';
import { StickyNoteCell } from '../../services/listRowShared';
import './NoteCell.css';

/**
 * One row's sticky note, edited in place. The author or an Admin edits it;
 * anyone else adds a new note, which becomes the row's note. A note above the
 * viewer's clearance reads "Restricted note" and can't be opened.
 */

function byline(note: StickyNoteCell): string | undefined {
  const author = note.authorName?.given?.[0];
  const date = note.sent && (dayjs(note.sent).isSame(dayjs(), 'day') ? 'today' : dayjs(note.sent).format('MMM D'));
  return [author, date].filter(Boolean).join(' · ') || undefined;
}

// The timeline renders a note from its rich text, so the one-line input goes in as one paragraph.
function toRichText(text: string): object {
  return { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text }] }] };
}

function stopRowClick(event: MouseEvent): void {
  event.stopPropagation();
}

export interface NoteCellProps {
  patientId?: string;
  note?: StickyNoteCell;
  /** Shows a saved note on its row before the queue reloads. */
  onSaved: (patientId: string, note: StickyNoteCell) => void;
}

export function NoteCell({ patientId, note, onSaved }: NoteCellProps): ReactElement | null {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner;
  const {
    currentUserPermissions: { canEditNote },
  } = useCurrentUser();
  // Undefined while not editing.
  const [draft, setDraft] = useState<string>();
  const [saving, setSaving] = useState(false);

  if (note?.restricted) {
    return <span className="queue-note-restricted">Restricted note</span>;
  }

  if (!patientId) {
    return null;
  }

  const canEdit = note ? canEditNote(note.communication) : false;
  const initial = canEdit ? (note?.text ?? '') : '';

  const save = async (): Promise<void> => {
    const text = draft?.trim();
    if (!text || text === initial) {
      setDraft(undefined);
      return;
    }

    setSaving(true);
    try {
      const saved =
        canEdit && note
          ? await updateTimelineNote(medplum, {
              noteRef: createReference(note.communication),
              content: text,
              richTextContent: toRichText(text),
            })
          : await createStandaloneNote(medplum, {
              currentUser: profile,
              subject: { reference: getReferenceString({ resourceType: 'Patient', id: patientId }) },
              content: text,
              richTextContent: JSON.stringify(toRichText(text)),
            });
      onSaved(patientId, {
        text,
        restricted: false,
        authorName: canEdit ? note?.authorName : profile.name?.[0],
        sent: saved.sent,
        communication: saved,
      });
      setDraft(undefined);
    } catch (error) {
      alert.error(`Couldn't save note: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  if (draft !== undefined) {
    return (
      <div className="queue-note is-editing" onClick={stopRowClick}>
        <input
          className="queue-note-input"
          aria-label="Note"
          placeholder="Add a note"
          autoFocus
          value={draft}
          disabled={saving}
          onChange={(event) => setDraft(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              void save();
            } else if (event.key === 'Escape') {
              setDraft(undefined);
            }
          }}
          onBlur={() => {
            if (draft.trim() === initial) {
              setDraft(undefined);
            }
          }}
        />
        <button
          type="button"
          className="queue-note-save"
          disabled={saving}
          // Keeps focus in the input, so blur doesn't close the editor first.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => void save()}
        >
          Save
        </button>
      </div>
    );
  }

  const open = (event: MouseEvent): void => {
    event.stopPropagation();
    setDraft(initial);
  };

  if (!note) {
    return (
      <button type="button" className="queue-note is-empty" onClick={open}>
        + Note
      </button>
    );
  }

  return (
    <Tooltip label={note.text} multiline maw={360} withArrow position="top-start" disabled={!note.text}>
      <button type="button" className="queue-note" onClick={open}>
        <span className="queue-note-text">{note.text}</span>
        <span className="queue-note-who">{byline(note)}</span>
      </button>
    </Tooltip>
  );
}
